import { formatCpfCnpj, unformatCpfCnpj } from '@/shared/formatters/document';
import { apiGet, apiPost } from '@/shared/utils/requestBuilder';

import type {
  Apuracao,
  ApuracaoAuditoriaEntry,
  ApuracaoStatus,
  CategoriaBreakdown,
  CategoriaEvento,
  EventoConsumo,
  EventoItemDetalhado,
  EventoItemStatus,
  FecharCicloFormData,
  ModalidadePostal,
  ProdutoPeriodicidade,
  ProdutoTipoUso,
  ReabrirCicloFormData,
  RejectedInvoice,
  ReprocessarEventosFormData,
  SaldoPacote,
  SnapshotContrato,
  ConfigHibridoApuracao,
  ConfigMensagemApuracao,
  TipoCliente,
  TrialContextApuracao,
} from '../types';

type ApiCycleStatus = 'OPEN' | 'CLOSED' | 'REOPENED';
type ApiTenantType = 'CONVENIADO' | 'AVULSO';
type ApiEventCategory =
  | 'NOTIFICATION'
  | 'NOTIFICATION_BATCH'
  | 'NEGOTIATION'
  | 'RETURN_RECEIPT'
  | 'RESEND'
  | 'SEDEX'
  | 'SUCCESS_FEE';
type ApiEventStatus = 'BILLABLE' | 'NOT_BILLABLE_TRIAL' | 'REVERSED';
type ApiEventSourceType = 'EXTRAJUDICIAL_ITEM' | 'NEGOTIATION_CASE' | 'OTHER';

interface ApiCycleListItem {
  id: string;
  workspaceId: string;
  tenantName: string;
  tenantType: ApiTenantType;
  contractId: string;
  productId: string;
  productName: string;
  productCategory: string | null;
  periodStart: string;
  periodEnd: string;
  status: ApiCycleStatus;
  contractStatus: 'ACTIVE' | 'TRIAL' | 'SUSPENDED' | 'CANCELED' | 'EXPIRED';
  inTrial: boolean;
  trialEnd: string | null;
  dueDay: number;
  totalBalance: number | null;
  usedBalance: number;
  overage: number;
  totalBillable: number;
  hasRejectedEvents: boolean;
  rejectedEventCount: number;
  closedAt: string | null;
  invoicedAt: string | null;
}

interface ApiCycleDetail extends ApiCycleListItem {
  contract: {
    id: string;
    status: string;
    finalValue: number | null;
    paymentMethod: string;
    dueDay: number;
    trialEnd: string | null;
  };
  product: {
    id: string;
    name: string;
    category: string | null;
    usageType: string;
    periodicity: string | null;
    unitPrice: number | null;
    includedUnits: number | null;
    overageUnitPrice: number | null;
    monthlyBasePrice: number | null;
    trialUnitLimit: number | null;
  };
  categoryBreakdown: Array<{
    category: ApiEventCategory;
    totalUnits: number;
    totalAmount: number;
    billableUnits: number;
    billableAmount: number;
    trialUnits: number;
    reversedUnits: number;
  }>;
  trialContext: { behavior: string | null; trialEnd: string | null } | null;
  packageInfo: {
    totalUnits: number;
    consumedUnits: number;
    remainingUnits: number;
    consumptionPercentage: number;
    nextResetAt: string | null;
  } | null;
  hybridInfo: {
    monthlyBasePrice: number;
    includedUnits: number;
    consumedUnits: number;
    overageUnits: number;
    overageUnitPrice: number;
    overageAmount: number;
  } | null;
  messageInfo: {
    includedMessages: number;
    consumedMessages: number;
    overageUnits: number;
    overageUnitPrice: number;
    overageAmount: number;
  } | null;
  auditTrail: ApiAuditTrailEntry[];
}

type ApiAuditAction =
  | 'CICLO_CRIADO'
  | 'EVENTO_APURADO'
  | 'CICLO_FECHADO'
  | 'CICLO_REABERTO'
  | 'FATURA_EMITIDA';

interface ApiAuditTrailEntry {
  id: string;
  action: ApiAuditAction;
  timestamp: string;
  userId: string | null;
  userName: string | null;
  motivo: string | null;
  metadata: Record<string, unknown> | null;
}

interface ApiEventListItem {
  id: string;
  cycleId: string;
  contractId: string;
  productId: string;
  category: ApiEventCategory;
  sourceType: ApiEventSourceType;
  sourceId: string | null;
  units: number;
  unitPrice: number | null;
  amountCharged: number | null;
  status: ApiEventStatus;
  reversedAt: string | null;
  reversalReason: string | null;
  createdAt: string;
  metadata: Record<string, unknown> | null;
}

type ApiRecipientStatus =
  | 'ENTREGUE'
  | 'PENDENTE'
  | 'EM_TRANSITO'
  | 'FALHA'
  | 'CANCELADO'
  | 'DESCONHECIDO';

interface ApiEventRecipient {
  id: string;
  name: string;
  document: string | null;
  status: ApiRecipientStatus;
  statusLabel: string;
}

interface ApiContractSnapshot {
  contractId: string;
  status: string;
  paymentMethod: string;
  dueDay: number;
  finalValue: number | null;
  modality: string | null;
  discountPercentage: number | null;
  postalModality: string | null;
}

interface ApiEventDetail extends ApiEventListItem {
  workspaceId: string;
  tenantName: string;
  productName: string;
  productCategory: string | null;
  rawPayload: Record<string, unknown>;
  contractSnapshot: ApiContractSnapshot;
  recipients: ApiEventRecipient[];
  rejectionReason: string | null;
  correctionSuggestion: string | null;
}

interface ApiListCyclesResponse {
  items: ApiCycleListItem[];
  summary: {
    openCycles: number;
    closingInSevenDays: number;
    closedThisMonth: number;
    withErrors: number;
    totalBillablePreview: number;
  };
}

function mapCycleStatus(status: ApiCycleStatus): ApuracaoStatus {
  if (status === 'OPEN') return 'ABERTO';
  if (status === 'CLOSED') return 'FECHADO';
  return 'REABERTO';
}

function mapTenantType(type: ApiTenantType): TipoCliente {
  return type;
}

function mapUsageType(usageType: string): ProdutoTipoUso {
  if (usageType === 'PACKAGE') return 'PACOTE';
  if (usageType === 'SUBSCRIPTION') return 'ASSINATURA';
  return 'POR_USO';
}

function mapPeriodicity(periodicity: string | null): ProdutoPeriodicidade {
  if (periodicity === 'MONTHLY') return 'MENSAL';
  if (periodicity === 'YEARLY') return 'ANUAL';
  if (periodicity === 'HYBRID') return 'HIBRIDO';
  return 'AVULSO';
}

function mapEventCategory(category: ApiEventCategory): CategoriaEvento {
  switch (category) {
    case 'NOTIFICATION':
      return 'NOTIFICACAO_ENVIADA';
    case 'NOTIFICATION_BATCH':
      return 'LOTE_NOTIFICACAO_CONCLUIDO';
    case 'NEGOTIATION':
      return 'NEGOCIACAO_COM_VALOR';
    case 'RETURN_RECEIPT':
      return 'AR_ENVIADO';
    case 'RESEND':
      return 'REENVIO_POSTAL';
    case 'SEDEX':
      return 'SEDEX';
    case 'SUCCESS_FEE':
      return 'TAXA_SUCESSO';
  }
}

function mapEventStatusToView(status: ApiEventStatus): EventoConsumo['status'] {
  if (status === 'BILLABLE') return 'APURADO';
  if (status === 'NOT_BILLABLE_TRIAL') return 'TRIAL_FREE';
  return 'NAO_COBRAVEL';
}

function mapSourceTypeToOrigem(sourceType: ApiEventSourceType): EventoConsumo['origemTipo'] {
  if (sourceType === 'EXTRAJUDICIAL_ITEM') return 'NOTIFICACAO';
  if (sourceType === 'NEGOTIATION_CASE') return 'NEGOCIACAO';
  return 'SISTEMA';
}

function projectBreakdown(
  detail: ApiCycleDetail,
  events: ApiEventListItem[],
): CategoriaBreakdown[] {
  const eventsByCategory = new Map<ApiEventCategory, string[]>();
  for (const event of events) {
    const arr = eventsByCategory.get(event.category) ?? [];
    arr.push(event.id);
    eventsByCategory.set(event.category, arr);
  }
  return detail.categoryBreakdown
    .filter((b) => b.totalUnits > 0)
    .map((b) => ({
      categoria: mapEventCategory(b.category),
      quantidade: b.totalUnits,
      valorUnitarioMedio: b.totalUnits > 0 ? b.totalAmount / b.totalUnits : null,
      subtotal: b.billableAmount,
      descontoTotal: 0,
      eventosIds: eventsByCategory.get(b.category) ?? [],
    }));
}

function buildOrigemLabel(apiEvent: ApiEventListItem): string {
  if (apiEvent.category === 'NOTIFICATION_BATCH') {
    const meta = apiEvent.metadata as Record<string, unknown> | null;
    const batchName = typeof meta?.batchName === 'string' ? meta.batchName : null;
    const batchCode = typeof meta?.batchCode === 'string' ? meta.batchCode : null;
    const itemCount = typeof meta?.itemCount === 'number' ? meta.itemCount : apiEvent.units;
    const title = batchCode ?? batchName ?? 'Lote';
    return `Lote ${title} (${itemCount} destinatários)`;
  }
  return apiEvent.sourceType;
}

function projectEvento(apiEvent: ApiEventListItem, cycleTenantName: string): EventoConsumo {
  return {
    id: apiEvent.id,
    apuracaoId: apiEvent.cycleId,
    tenantId: '',
    tenantNome: cycleTenantName,
    produtoId: apiEvent.productId,
    produtoNome: null,
    categoria: mapEventCategory(apiEvent.category),
    status: mapEventStatusToView(apiEvent.status),
    timestamp: apiEvent.createdAt,
    quantidade: apiEvent.units,
    valorUnitario: apiEvent.unitPrice,
    valorCobravel: apiEvent.amountCharged ?? 0,
    descontoAplicado: 0,
    origemId: apiEvent.sourceId ?? '',
    origemTipo:
      apiEvent.category === 'NOTIFICATION_BATCH'
        ? 'LOTE'
        : mapSourceTypeToOrigem(apiEvent.sourceType),
    origemLabel: buildOrigemLabel(apiEvent),
    motivoRejeicao: null,
    sugestaoCorrecao: null,
    snapshotContrato: null,
    payloadBruto: (apiEvent.metadata as Record<string, unknown>) ?? {},
    itensDetalhados: null,
    itensTituloRotulo: null,
  };
}

function mapRecipientStatus(status: ApiRecipientStatus): EventoItemStatus {
  if (status === 'ENTREGUE') return 'SUCESSO';
  if (status === 'FALHA') return 'FALHA';
  if (status === 'PENDENTE') return 'PENDENTE';
  return 'INFO';
}

function buildRecipientDescricao(document: string | null): string | null {
  if (!document) return null;
  const digits = unformatCpfCnpj(document);
  const prefix = digits.length > 11 ? 'CNPJ' : 'CPF';
  return `${prefix} ${formatCpfCnpj(document)}`;
}

function projectRecipientsToItens(recipients: ApiEventRecipient[]): EventoItemDetalhado[] {
  return recipients.map((r) => ({
    id: r.id,
    label: r.name,
    descricao: buildRecipientDescricao(r.document),
    status: mapRecipientStatus(r.status),
    statusLabel: r.statusLabel,
    valor: null,
    metadata: null,
  }));
}

function projectSnapshotContrato(snapshot: ApiContractSnapshot): SnapshotContrato | null {
  if (
    !snapshot.contractId &&
    snapshot.modality == null &&
    snapshot.discountPercentage == null &&
    snapshot.postalModality == null
  ) {
    return null;
  }
  return {
    contratoId: snapshot.contractId,
    valorCustomizado: snapshot.finalValue,
    percentualDesconto: snapshot.discountPercentage,
    modalidadePostal: (snapshot.postalModality as ModalidadePostal | null) ?? null,
    boletoCorreio: null,
    modalidade: snapshot.modality,
  };
}

function projectEventoDetalhe(apiDetail: ApiEventDetail): EventoConsumo {
  const base = projectEvento(apiDetail, apiDetail.tenantName);
  const isBatch = apiDetail.category === 'NOTIFICATION_BATCH';
  return {
    ...base,
    tenantNome: apiDetail.tenantName,
    produtoNome: apiDetail.productName,
    sugestaoCorrecao: apiDetail.correctionSuggestion,
    snapshotContrato: projectSnapshotContrato(apiDetail.contractSnapshot),
    payloadBruto: apiDetail.rawPayload,
    itensDetalhados:
      apiDetail.recipients.length > 0 ? projectRecipientsToItens(apiDetail.recipients) : null,
    itensTituloRotulo: isBatch ? 'Destinatários do lote' : 'Destinatários',
  };
}

function projectPackageInfo(detail: ApiCycleDetail): SaldoPacote | null {
  if (!detail.packageInfo) return null;
  return {
    total: detail.packageInfo.totalUnits,
    consumido: detail.packageInfo.consumedUnits,
    percentualConsumido: detail.packageInfo.consumptionPercentage,
    proximoReset: detail.packageInfo.nextResetAt ?? detail.periodEnd,
    alertaPercentual: 80,
  };
}

function projectHybridInfo(detail: ApiCycleDetail): ConfigHibridoApuracao | null {
  if (!detail.hybridInfo) return null;
  return {
    monthlyBasePrice: detail.hybridInfo.monthlyBasePrice,
    includedUnits: detail.hybridInfo.includedUnits,
    unidadesUsadas: detail.hybridInfo.consumedUnits,
    overageUnitPrice: detail.hybridInfo.overageUnitPrice,
    unidadesExcedentes: detail.hybridInfo.overageUnits,
    valorExcedenteTotal: detail.hybridInfo.overageAmount,
  };
}

export function projectMessageInfo(detail: ApiCycleDetail): ConfigMensagemApuracao | null {
  if (!detail.messageInfo) return null;
  return {
    mensagensIncluidas: detail.messageInfo.includedMessages,
    mensagensUsadas: detail.messageInfo.consumedMessages,
    mensagensExcedentes: detail.messageInfo.overageUnits,
    valorMensagemExcedente: detail.messageInfo.overageUnitPrice,
    valorExcedenteTotal: detail.messageInfo.overageAmount,
  };
}

function projectTrialContext(detail: ApiCycleDetail): TrialContextApuracao | null {
  if (!detail.trialContext) return null;
  const behavior = (detail.trialContext.behavior ??
    'FREE') as TrialContextApuracao['comportamento'];
  return {
    trialId: detail.contractId,
    comportamento: behavior,
    dataExpiracao: detail.trialContext.trialEnd ?? detail.periodEnd,
  };
}

function projectCycleListToApuracao(item: ApiCycleListItem): Apuracao {
  return {
    id: item.id,
    tenantId: item.workspaceId,
    tenantNome: item.tenantName,
    contratoId: item.contractId,
    produtoId: item.productId,
    produtoNome: item.productName,
    produtoTipoUso: 'POR_USO',
    produtoPeriodicidade: 'MENSAL',
    tipoCliente: mapTenantType(item.tenantType),
    diaVencimento: item.dueDay,
    periodoInicio: item.periodStart,
    periodoFim: item.periodEnd,
    status: mapCycleStatus(item.status),
    totalCobravel: item.totalBillable,
    totalEventos: item.usedBalance,
    eventosRejeitadosCount: item.rejectedEventCount,
    eventosNaoCobraveisCount: 0,
    breakdown: [],
    eventos: [],
    auditoria: [],
    saldoPacote: null,
    configHibrido: null,
    configMensagem: null,
    trialContext: item.inTrial
      ? {
          trialId: item.contractId,
          comportamento: 'FREE',
          dataExpiracao: item.trialEnd ?? item.periodEnd,
        }
      : null,
    pacoteAlerta: false,
    faturaEmitida: !!item.invoicedAt,
    fechadoEm: item.closedAt,
    fechadoPor: null,
    reabertoEm: null,
    reabertoPor: null,
    justificativaReabertura: null,
    criadoEm: item.periodStart,
    atualizadoEm: item.closedAt ?? item.periodStart,
  };
}

function projectAuditTrail(entries: ApiAuditTrailEntry[] | undefined): ApuracaoAuditoriaEntry[] {
  if (!entries) return [];
  return entries.map((entry) => {
    const meta = entry.metadata ?? {};
    const sourceType = typeof meta.sourceType === 'string' ? meta.sourceType : null;
    const descricao = buildAuditDescricao(entry, sourceType);
    return {
      id: entry.id,
      acao: entry.action,
      usuarioId: entry.userId,
      usuarioNome: entry.userName,
      timestamp: entry.timestamp,
      descricao,
      detalhes: entry.motivo
        ? { ...meta, motivo: entry.motivo }
        : Object.keys(meta).length > 0
          ? meta
          : null,
    };
  });
}

function buildAuditDescricao(entry: ApiAuditTrailEntry, sourceType: string | null): string {
  switch (entry.action) {
    case 'CICLO_CRIADO':
      return 'Ciclo iniciado.';
    case 'CICLO_FECHADO':
      return 'Ciclo fechado pelo responsável.';
    case 'CICLO_REABERTO':
      return entry.motivo
        ? `Ciclo reaberto. A fatura vinculada será reemitida no próximo fechamento. Motivo: ${entry.motivo}`
        : 'Ciclo reaberto. A fatura vinculada será reemitida no próximo fechamento.';
    case 'FATURA_EMITIDA':
      return 'Fatura emitida para este ciclo.';
    case 'EVENTO_APURADO': {
      const origem =
        sourceType === 'NEGOTIATION'
          ? 'negociação'
          : sourceType === 'NOTIFICATION'
            ? 'notificação'
            : 'evento';
      return `Consumo registrado: 1 ${origem} apurada.`;
    }
  }
}

function projectCycleDetailToApuracao(
  detail: ApiCycleDetail,
  events: ApiEventListItem[],
): Apuracao {
  const base = projectCycleListToApuracao(detail);
  return {
    ...base,
    produtoTipoUso: mapUsageType(detail.product.usageType),
    produtoPeriodicidade: mapPeriodicity(detail.product.periodicity),
    breakdown: projectBreakdown(detail, events),
    eventos: events.map((event) => projectEvento(event, detail.tenantName)),
    saldoPacote: projectPackageInfo(detail),
    configHibrido: projectHybridInfo(detail),
    configMensagem: projectMessageInfo(detail),
    trialContext: projectTrialContext(detail),
    pacoteAlerta: detail.packageInfo != null && detail.packageInfo.consumptionPercentage >= 80,
    auditoria: projectAuditTrail(detail.auditTrail),
  };
}

async function readJson<T>(response: Response, fallbackMessage: string): Promise<T> {
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(json?.error?.message ?? fallbackMessage);
  }
  return json.data as T;
}

export const apuracaoService = {
  async fetchAll(): Promise<Apuracao[]> {
    const response = await apiGet('/accounting/cycles');
    const data = await readJson<ApiListCyclesResponse>(response, 'Falha ao listar apurações');
    return data.items.map(projectCycleListToApuracao);
  },

  async fetchById(apuracaoId: string): Promise<Apuracao> {
    const [detailRes, eventsRes] = await Promise.all([
      apiGet(`/accounting/cycles/${apuracaoId}`),
      apiGet(`/accounting/cycles/${apuracaoId}/events`),
    ]);
    const detail = await readJson<ApiCycleDetail>(detailRes, 'Falha ao carregar apuração');
    const events = await readJson<ApiEventListItem[]>(eventsRes, 'Falha ao carregar eventos');
    return projectCycleDetailToApuracao(detail, events);
  },

  async fetchEventoDetalhe(eventoId: string): Promise<EventoConsumo> {
    const response = await apiGet(`/accounting/events/${eventoId}`);
    const detail = await readJson<ApiEventDetail>(response, 'Falha ao carregar detalhe do evento');
    return projectEventoDetalhe(detail);
  },

  async fetchRejeitadosDoCiclo(cycleId: string): Promise<RejectedInvoice[]> {
    const response = await apiGet(`/accounting/cycles/${cycleId}/rejected`);
    return readJson<RejectedInvoice[]>(response, 'Falha ao listar faturas rejeitadas');
  },

  async reemitirFaturaRejeitada(invoiceId: string): Promise<void> {
    const response = await apiPost(`/billing/${invoiceId}/retry-asaas`, {});
    await readJson<unknown>(response, 'Falha ao reemitir fatura no Asaas');
  },

  async fecharCiclo(data: FecharCicloFormData): Promise<Apuracao> {
    const response = await apiPost(`/accounting/cycles/${data.apuracaoId}/close`, {
      acknowledgesRejectedEvents: data.cienteEventosRejeitados,
    });
    const detail = await readJson<ApiCycleDetail>(response, 'Falha ao fechar ciclo');
    const eventsRes = await apiGet(`/accounting/cycles/${data.apuracaoId}/events`);
    const events = await readJson<ApiEventListItem[]>(eventsRes, 'Falha ao carregar eventos');
    return projectCycleDetailToApuracao(detail, events);
  },

  async reabrirCiclo(data: ReabrirCicloFormData): Promise<Apuracao> {
    const response = await apiPost(`/accounting/cycles/${data.apuracaoId}/reopen`, {
      justification: data.justificativa,
      acknowledgesImpact: data.cienteImpacto,
    });
    const detail = await readJson<ApiCycleDetail>(response, 'Falha ao reabrir ciclo');
    const eventsRes = await apiGet(`/accounting/cycles/${data.apuracaoId}/events`);
    const events = await readJson<ApiEventListItem[]>(eventsRes, 'Falha ao carregar eventos');
    return projectCycleDetailToApuracao(detail, events);
  },

  async marcarApuracaoFaturada(apuracaoId: string, faturaNumero: string): Promise<Apuracao> {
    const response = await apiPost(`/accounting/cycles/${apuracaoId}/mark-invoiced`, {
      invoiceReference: faturaNumero,
    });
    const detail = await readJson<ApiCycleDetail>(response, 'Falha ao marcar fatura emitida');
    const eventsRes = await apiGet(`/accounting/cycles/${apuracaoId}/events`);
    const events = await readJson<ApiEventListItem[]>(eventsRes, 'Falha ao carregar eventos');
    return projectCycleDetailToApuracao(detail, events);
  },

  async reprocessarEventos(data: ReprocessarEventosFormData): Promise<EventoConsumo[]> {
    const response = await apiPost('/accounting/events/reprocess', {
      eventIds: data.eventosIds,
    });
    await readJson<{ reprocessed: number }>(response, 'Falha ao reprocessar eventos');
    return [];
  },
};
